import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { OperationSubmission } from '../../../models/operation-submission';

interface PhotoEntry { label: string; url: string; }

// Reuses the end-ride verification details layout and styles
@Component({
  selector: 'app-operation-submission-details',
  imports: [CommonModule, RouterLink],
  templateUrl: './operation-submission-details.html',
  styleUrls: [
    '../ride-verification-details/ride-verification-details.scss',
    './operation-submission-details.scss',
  ]
})
export class OperationSubmissionDetails {

  @Input({ required: true }) submission: OperationSubmission;
  @Input() currentIndex = 0;
  @Input() total = 0;
  @Input() showNav = false;
  @Input() canReview = false;

  @Output() approve = new EventEmitter<void>();
  @Output() reject = new EventEmitter<void>();
  @Output() prev = new EventEmitter<void>();
  @Output() next = new EventEmitter<void>();

  get isPending(): boolean {
    return this.submission?.status === 'PENDING';
  }

  get hasPrev(): boolean {
    return this.currentIndex > 0;
  }

  get hasNext(): boolean {
    return this.currentIndex < this.total - 1;
  }

  get statusClass(): string {
    switch (this.submission?.status) {
      case 'APPROVED': return 'status--approved';
      case 'REJECTED': return 'status--rejected';
      default: return 'status--pending';
    }
  }

  get photos(): PhotoEntry[] {
    return [...(this.submission?.photos ?? [])]
      .sort((a, b) => a.slot - b.slot)
      .map(ph => ({ label: `Photo ${ph.slot}`, url: ph.url }));
  }

  get driverInitials(): string {
    const parts = (this.submission?.employeeName ?? '').trim().split(/\s+/);
    const f = parts[0]?.[0] ?? '';
    const l = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (f + l).toUpperCase() || '?';
  }

  // ── Lightbox ──────────────────────────────────────────────────
  lightboxOpen = false;
  lightboxIndex = 0;

  openLightbox(index: number): void {
    this.lightboxIndex = index;
    this.lightboxOpen = true;
  }

  lightboxPrev(): void {
    if (this.lightboxIndex > 0) this.lightboxIndex--;
  }

  lightboxNext(): void {
    if (this.lightboxIndex < this.photos.length - 1) this.lightboxIndex++;
  }

  get lightboxPhoto(): PhotoEntry | null {
    return this.photos[this.lightboxIndex] ?? null;
  }

  openMap(lat: number | null | undefined, lng: number | null | undefined): void {
    if (lat && lng) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
    }
  }
}
